import { endMinute, startMinute, type Opportunity } from '../../shared/opportunity';
import { isPersonal } from './personalEvents';

/**
 * How much of an event you are actually going to.
 *
 * Almost nobody attends an all-day summit all day. Before this, the schedule
 * insisted they did: a nine-hour block sat across the column, made every other
 * event on that day look like a clash, and left no way to say "I'm there for
 * the morning". The event keeps its published length — that is a fact about
 * the event — and attendance is a separate span inside it.
 *
 * The distinction is the same one travel makes between a window and a journey,
 * with one deliberate difference. A travel window is scenery: it does not take
 * a lane, because the time is yours whether or not you are moving through it.
 * An event window *does* take a lane, because the event is genuinely running
 * then and anything overlapping it genuinely overlaps.
 */

/** Trims land on quarter-hours. Finer would be false precision. */
export const TRIM_STEP_MINUTES = 15;

/** The shortest attendance worth recording. Below this, just remove the event. */
export const MIN_ATTENDANCE_MINUTES = 15;

/** What the user chose for one event. Absent ends mean "as published". */
export interface Trim {
  start?: number;
  end?: number;
}

export type Trims = Record<string, Trim>;

export const eventStart = (e: Opportunity) => startMinute(e) ?? 0;
export const eventEnd = (e: Opportunity) => endMinute(e) ?? eventStart(e) + 60;

export const snapTrim = (m: number) =>
  Math.round(m / TRIM_STEP_MINUTES) * TRIM_STEP_MINUTES;

/**
 * The span the user is actually there for.
 *
 * The answer for anything that reasons about when they are *busy* — lane
 * layout, and whether two events collide. A clash between two published
 * windows that the user has already resolved, by leaving one early, is not a
 * clash; flagging it anyway is how a planner learns to ignore the flag.
 *
 * A personal event has no published hours to be trimmed inside of, so its own
 * times are the answer whatever a stale trim might say about it.
 */
export function attendedSpan(event: Opportunity, trims: Trims): { start: number; end: number } {
  const { start, end } = attendanceFor(event, isPersonal(event) ? undefined : trims[event.id]);
  return { start, end };
}

export interface Attendance {
  /** When you arrive. */
  start: number;
  /** When you leave. */
  end: number;
  /** Minutes after the event's own start. Zero when arriving on time. */
  lateBy: number;
  /** Minutes before the event's own end. Zero when staying to the finish. */
  earlyBy: number;
  /** True when either end has been moved. */
  trimmed: boolean;
}

/**
 * Resolves a stored trim against the event, clamping it into range.
 *
 * Clamped on read as well as on write: an event's published times can change
 * under a stored trim when the listing is corrected mid-week, and a trim that
 * suddenly sits outside its event should be brought back rather than drawn
 * somewhere impossible.
 */
export function attendanceFor(event: Opportunity, trim: Trim | undefined): Attendance {
  const from = eventStart(event);
  const to = eventEnd(event);

  const start = Math.min(
    Math.max(trim?.start ?? from, from),
    Math.max(from, to - MIN_ATTENDANCE_MINUTES),
  );
  const end = Math.max(
    Math.min(trim?.end ?? to, to),
    Math.min(to, start + MIN_ATTENDANCE_MINUTES),
  );

  return {
    start,
    end,
    lateBy: start - from,
    earlyBy: to - end,
    trimmed: start > from || end < to,
  };
}

/**
 * How much of an event is skipped, as the schedule card's note and the
 * callout's steppers both say it: "30 mins", "1 hr", "1 hr 15 mins", "2 hrs".
 */
export function trimLength(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  const hrs = h ? `${h} hr${h > 1 ? 's' : ''}` : '';
  const mins = m ? `${m} min${m > 1 ? 's' : ''}` : '';
  return [hrs, mins].filter(Boolean).join(' ');
}

/** Where a dragged edge may legally land. */
export function trimBounds(
  event: Opportunity,
  edge: 'start' | 'end',
  other: number,
): { min: number; max: number } {
  const from = eventStart(event);
  const to = eventEnd(event);

  return edge === 'start'
    ? { min: from, max: Math.max(from, other - MIN_ATTENDANCE_MINUTES) }
    : { min: Math.min(to, other + MIN_ATTENDANCE_MINUTES), max: to };
}
